import { FormikHelpers } from 'formik';
import { useCreateOrder, useSaveOrder, useSendRFQ, useToast } from 'hooks';
import { mapParameters } from 'utils/parameters';
import { Modal, ModalBody, ModalHeader } from 'components/UI';
import { ActiveParameters } from 'types';
import { anonymousUser } from 'constants/users';
import { useEffect } from 'react';
import { IProductResource } from '../../../../server/types/models/IProduct';
import { IOrderCustomerData } from '../../../../server/types/models/IOrder';
import { IRFQEmail } from '../../../../server/types';
import OrderForm, { OrderFormValues } from './OrderForm';

type OrderConfigModalProps = {
  data?: OrderFormValues | null;
  rfq?: string | number | boolean | undefined;
  isOpen: boolean;
  product: IProductResource;
  viewParameters?: ActiveParameters;
  onClose: () => void;
  resultFileJobId: string;
  selectedProduct: string;
  resultFileNameRef: string;
};

const OrderConfigModal = ({
  data,
  rfq,
  isOpen = false,
  product,
  viewParameters,
  onClose,
  resultFileJobId,
  selectedProduct,
  resultFileNameRef,
}: OrderConfigModalProps) => {
  const [showToast] = useToast();
  const [sendRFQ] = useSendRFQ();
  const [createOrder, { error: createOrderError }] = useCreateOrder();
  const [saveOrder, { error: saveOrderError }] = useSaveOrder();

  const formType = rfq ? 'RFQ' : 'Order';
  const title = rfq ? 'Request for Quotation' : 'Order Configuration';

  const orderConfiguration = async (
    jobId: string,
    customerData: IOrderCustomerData,
    modelParameters: ActiveParameters
  ) => {
    const { tempBucket, saveToBucket } = product as IProductResource;

    await createOrder({
      bucketName: saveToBucket,
      productKey: selectedProduct,
      objectName: resultFileNameRef,
      jobId,
      parameters: modelParameters,
      productName: customerData.productName,
      customerData,
    });

    const body = {
      jobId,
      tempBucketName: tempBucket,
      saveBucketName: saveToBucket,
    };

    return saveOrder(body);
  };

  const handleSubmit = async (
    values: OrderFormValues | Record<string, never>,
    { setSubmitting }: FormikHelpers<OrderFormValues | Record<string, never>>
  ) => {
    if (!product.id) return;

    const customerData = {
      customerFirstName: values.firstName,
      customerLastName: values.lastName,
      customerEmail: values.email,
      zipCode: values.zipCode,
      companyName: values.company,
      customerPhone: values.phone,
      country: values.country,
      state: values.state,
      productName: product.displayName,
      productId: product.id,
      specialRequests: values.specialRequests,
      parameterGroups:
        product.parameters && product.parameterList && viewParameters
          ? mapParameters(viewParameters, product.parameterList)
          : null,
    };

    await orderConfiguration(resultFileJobId, customerData, viewParameters as ActiveParameters);

    await sendRFQ(customerData as IRFQEmail);

    setSubmitting(false);
    onClose();

    showToast({
      message: `${formType} submitted successfully.`,
    });
  };

  useEffect(() => {
    const message = createOrderError ?? saveOrderError;

    if (message) {
      showToast({
        status: 'error',
        message,
      });
    }
  }, [createOrderError, saveOrderError]);

  return (
    <Modal
      isOpen={isOpen}
      isCloseButton
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isCentered
      size="2xl"
      onClose={onClose}
    >
      <ModalHeader pt={5} textAlign="center" fontSize="4xl">
        {title}
      </ModalHeader>
      <ModalBody px={6} pt={5} pb={8}>
        <OrderForm
          onSubmit={handleSubmit}
          buttonText={rfq ? 'SUBMIT RFQ' : 'SUBMIT ORDER'}
          data={data?.email === anonymousUser.email ? null : data}
        />
      </ModalBody>
    </Modal>
  );
};

export default OrderConfigModal;
